/**
 * JALRAKSHAK Real-Time Dashboard & Telemetry Visualizer
 */

import { SENSORS_CONFIG } from '../data/sensorsData.js';

let chartAnimationFrame = null;
const HISTORY_POINTS = 20;

// Data streams for live canvas rendering
const streamData = {
  ph: Array.from({ length: HISTORY_POINTS }, () => 7.3 + (Math.random() * 0.15 - 0.07)),
  do: Array.from({ length: HISTORY_POINTS }, () => 6.8 + (Math.random() * 0.2 - 0.1)),
  turbidity: Array.from({ length: HISTORY_POINTS }, () => 3.2 + (Math.random() * 0.3 - 0.15))
};

export function initDashboard() {
  initWqiGauge(89);
  initRealtimeCanvasChart();
  initTelemetryTicker();
  initRadarPlot();
}

/**
 * Animate the Circular WQI Gauge (0-100)
 */
export function initWqiGauge(score = 89) {
  const circle = document.querySelector('.gauge-progress-circle');
  const scoreDisplay = document.querySelector('.gauge-score');
  const labelDisplay = document.querySelector('.gauge-quality-status');

  if (!circle || !scoreDisplay) return;

  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  circle.style.strokeDasharray = `${circumference} ${circumference}`;

  const offset = circumference - (score / 100) * circumference;
  circle.style.strokeDashoffset = offset;

  scoreDisplay.textContent = score;

  if (labelDisplay) {
    if (score >= 80) {
      labelDisplay.textContent = 'EXCELLENT';
      labelDisplay.style.color = '#10B981';
      circle.style.stroke = '#10B981';
    } else if (score >= 50) {
      labelDisplay.textContent = 'MODERATE';
      labelDisplay.style.color = '#F59E0B';
      circle.style.stroke = '#F59E0B';
    } else {
      labelDisplay.textContent = 'POLLUTED';
      labelDisplay.style.color = '#EF4444';
      circle.style.stroke = '#EF4444';
    }
  }
}

/**
 * High-Performance Dynamic Vector SVG Line Chart Engine
 * 100% reliable, sharp at any DPI, no canvas crashes or browser extension blocks.
 */
function initRealtimeCanvasChart() {
  const phLine = document.getElementById('svg-ph-line');
  const doLine = document.getElementById('svg-do-line');
  const turbLine = document.getElementById('svg-turb-line');
  const phArea = document.getElementById('svg-ph-area');
  const doArea = document.getElementById('svg-do-area');
  const phDot = document.getElementById('svg-ph-dot');
  const doDot = document.getElementById('svg-do-dot');

  if (!phLine) return;

  const width = 540;
  const paddingLeft = 45;
  const chartHeight = 160;
  const topOffset = 25;
  const bottomY = topOffset + chartHeight;

  function generateSmoothPath(data, min, max) {
    const stepX = (width - paddingLeft) / (data.length - 1);
    let path = '';
    let lastX = paddingLeft;
    let lastY = bottomY;

    data.forEach((val, i) => {
      const norm = Math.max(0, Math.min(1, (val - min) / (max - min)));
      const x = paddingLeft + i * stepX;
      const y = bottomY - (norm * chartHeight);

      if (i === 0) {
        path += `M ${x.toFixed(1)} ${y.toFixed(1)}`;
      } else {
        const prevX = paddingLeft + (i - 1) * stepX;
        const prevNorm = Math.max(0, Math.min(1, (data[i - 1] - min) / (max - min)));
        const prevY = bottomY - (prevNorm * chartHeight);
        const cpX = (prevX + x) / 2;
        path += ` C ${cpX.toFixed(1)} ${prevY.toFixed(1)}, ${cpX.toFixed(1)} ${y.toFixed(1)}, ${x.toFixed(1)} ${y.toFixed(1)}`;
      }
      lastX = x;
      lastY = y;
    });

    return { linePath: path, lastX, lastY };
  }

  function renderSvgChart() {
    // 1. pH (Range 5.0 - 9.5)
    const phResult = generateSmoothPath(streamData.ph, 5.0, 9.5);
    if (phLine) phLine.setAttribute('d', phResult.linePath);
    if (phArea) phArea.setAttribute('d', `${phResult.linePath} L ${phResult.lastX.toFixed(1)} ${bottomY} L ${paddingLeft} ${bottomY} Z`);
    if (phDot) {
      phDot.setAttribute('cx', phResult.lastX.toFixed(1));
      phDot.setAttribute('cy', phResult.lastY.toFixed(1));
    }

    // 2. DO (Range 1.0 - 10.0)
    const doResult = generateSmoothPath(streamData.do, 1.0, 10.0);
    if (doLine) doLine.setAttribute('d', doResult.linePath);
    if (doArea) doArea.setAttribute('d', `${doResult.linePath} L ${doResult.lastX.toFixed(1)} ${bottomY} L ${paddingLeft} ${bottomY} Z`);
    if (doDot) {
      doDot.setAttribute('cx', doResult.lastX.toFixed(1));
      doDot.setAttribute('cy', doResult.lastY.toFixed(1));
    }

    // 3. Turbidity (Range 0 - 35)
    const turbResult = generateSmoothPath(streamData.turbidity, 0, 35);
    if (turbLine) turbLine.setAttribute('d', turbResult.linePath);
  }

  // Live streaming interval
  setInterval(() => {
    // Shift & append new points
    streamData.ph.shift();
    const lastPh = streamData.ph[streamData.ph.length - 1];
    streamData.ph.push(Number((lastPh + (Math.random() * 0.1 - 0.05)).toFixed(2)));

    streamData.do.shift();
    const lastDo = streamData.do[streamData.do.length - 1];
    streamData.do.push(Number((lastDo + (Math.random() * 0.12 - 0.06)).toFixed(2)));

    streamData.turbidity.shift();
    const lastTurb = streamData.turbidity[streamData.turbidity.length - 1];
    streamData.turbidity.push(Number((lastTurb + (Math.random() * 0.15 - 0.07)).toFixed(2)));

    renderSvgChart();
  }, 1200);

  renderSvgChart();
}

/**
 * Live Telemetry Ticker (Battery, Solar Flux, Speed, Time)
 */
function initTelemetryTicker() {
  const timeDisplay = document.getElementById('telemetry-clock');
  const solarDisplay = document.getElementById('telemetry-solar-flux');
  const speedDisplay = document.getElementById('telemetry-speed');

  function updateClock() {
    const now = new Date();
    if (timeDisplay) {
      timeDisplay.textContent = now.toTimeString().split(' ')[0] + ' UTC+5:30';
    }
  }

  setInterval(updateClock, 1000);
  updateClock();

  // Subtle telemetry variance
  setInterval(() => {
    if (solarDisplay) {
      const flux = 340 + Math.floor(Math.random() * 25 - 12);
      solarDisplay.textContent = `${flux} W (Active Array)`;
    }
    if (speedDisplay) {
      const spd = (1.8 + (Math.random() * 0.2 - 0.1)).toFixed(1);
      speedDisplay.textContent = `${spd} kn (Lawnmower Pattern)`;
    }
  }, 3000);
}

/**
 * Interactive GPS Radar Map Simulation
 */
function initRadarPlot() {
  const radarMap = document.querySelector('.radar-map-display');
  if (!radarMap) return;

  const beacon = document.createElement('div');
  beacon.className = 'asv-radar-beacon';
  beacon.style.position = 'absolute';
  beacon.style.width = '12px';
  beacon.style.height = '12px';
  beacon.style.borderRadius = '50%';
  beacon.style.backgroundColor = '#00F0FF';
  beacon.style.boxShadow = '0 0 15px #00F0FF, 0 0 30px #00D2FF';
  beacon.style.top = '48%';
  beacon.style.left = '52%';
  beacon.style.transition = 'all 2s ease';

  radarMap.appendChild(beacon);

  // Subtle wandering waypoint simulation
  setInterval(() => {
    const newTop = 40 + Math.random() * 20;
    const newLeft = 45 + Math.random() * 20;
    beacon.style.top = `${newTop}%`;
    beacon.style.left = `${newLeft}%`;
  }, 4000);
}
